import { motion } from "framer-motion";
import {
  Check,
  CreditCard,
  MonitorSmartphone,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import { VARIANT, VIP_ACQUISTA_URL, VIP_REGISTER_URL } from "@/constants/links";
import { COMPATIBILITY, PRICING } from "@/content/vip-capabilities";
import { track } from "@/lib/tracker";

const isVip = VARIANT === "vip";

const Star = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12 2l2.4 6.9L21 11l-6.6 2.1L12 20l-2.4-6.9L3 11l6.6-2.1L12 2z" />
  </svg>
);

/**
 * Sezione compatibilita' + prezzo (solo variante VIP), ispirata ai blocchi
 * "Compatibility" e pricing di ClevGuard: elenca requisiti reali del prodotto
 * (Android 7+, no root, APK, multi-device) e il piano con cio' che include.
 * I 3 passi di attivazione sono gia' in HowItWorks: qui NON si ripetono.
 */
export const Compatibility = () => {
  if (!isVip) return null;

  return (
    <section
      id="compatibilita"
      className="relative scroll-mt-24 overflow-hidden bg-white py-24 md:py-32"
    >
      <div aria-hidden="true" className="absolute -left-24 top-16 h-52 w-52 rounded-full bg-softpurple" />
      <div aria-hidden="true" className="absolute -right-20 bottom-20 h-44 w-44 rounded-full border-[14px] border-yellow" />
      <Star className="animate-float-slow absolute right-[8%] top-16 hidden h-8 w-8 text-pinky-deep lg:block" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mx-auto mb-14 max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#5f4bc9] px-4 py-2 text-xs font-extrabold uppercase tracking-widest text-white shadow-lg shadow-violet/30">
            <MonitorSmartphone size={14} /> Compatibilità e prezzo
          </span>
          <h2 className="isolate mt-5 font-display text-3xl font-black tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Funziona su <span className="text-highlight">Android</span>, senza root
          </h2>
          <p className="mt-4 text-base font-medium leading-relaxed text-ink/75 sm:text-lg">
            Requisiti reali del prodotto e cosa include il piano VIP. Un solo prezzo,
            tutte le capacità di supervisione incluse.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1fr_1fr]">
          {/* Tabella compatibilità */}
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            data-testid="compat-table"
            className="overflow-hidden rounded-[2rem] border-[3px] border-ink/10 bg-background"
          >
            <div className="flex items-center gap-3 bg-ink px-6 py-4 text-white">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-yellow text-ink">
                <Smartphone size={18} />
              </span>
              <h3 className="font-display text-lg font-black tracking-tight">Requisiti e compatibilità</h3>
            </div>
            <dl className="divide-y divide-ink/8">
              {COMPATIBILITY.map((row) => (
                <div
                  key={row.label}
                  className="flex flex-wrap items-baseline justify-between gap-2 px-6 py-3.5"
                >
                  <dt className="text-sm font-bold text-ink/70">{row.label}</dt>
                  <dd className="text-right text-sm font-extrabold text-ink">{row.value}</dd>
                </div>
              ))}
            </dl>
            <p className="flex items-center gap-2 bg-softgreen px-6 py-3.5 text-[13px] font-bold text-emerald-700">
              <ShieldCheck size={15} />
              Verificato su Samsung, Xiaomi, OPPO, Realme e TCL
            </p>
          </motion.div>

          {/* Prezzo + incluso */}
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            data-testid="compat-pricing"
            className="relative overflow-hidden rounded-[2rem] bg-[#5f4bc9] px-7 py-9 text-white shadow-xl shadow-violet/25"
          >
            <div aria-hidden="true" className="absolute inset-0 bg-dots-light opacity-50" />
            <Star className="animate-float absolute right-8 top-8 hidden h-7 w-7 text-yellow md:block" />
            <div className="relative">
              <p className="font-mono text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#ffe06a]">
                Piano VIP · tutto incluso
              </p>
              <p className="mt-3 flex items-baseline gap-2">
                <span className="font-display text-5xl font-black tracking-tight sm:text-6xl">
                  {PRICING.monthly}
                </span>
                <span className="text-base font-bold text-white/90">{PRICING.per}</span>
              </p>
              <p className="mt-2 text-sm font-medium text-white/85">{PRICING.billing}</p>

              <ul className="mt-7 space-y-2.5">
                {PRICING.includes.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2.5 text-[14px] font-semibold text-white/90"
                  >
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-yellow text-ink">
                      <Check size={11} strokeWidth={3.5} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  data-testid="compat-acquista-btn"
                  href={VIP_ACQUISTA_URL}
                  onClick={() => track("acquista_click")}
                  className="btn-3d-yellow inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-extrabold"
                >
                  <CreditCard size={17} />
                  Acquista VIP
                </a>
                <a
                  data-testid="compat-register-btn"
                  href={VIP_REGISTER_URL}
                  onClick={() => track("register_click")}
                  className="btn-3d-cream inline-flex items-center justify-center rounded-full px-6 py-3.5 text-sm font-extrabold"
                >
                  Crea account
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
